/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Component } from 'react';
import { Select, AutoComplete, message, Icon, Input, Tooltip } from 'antd';
import { getLicenseNumbers, getStoredLicenseNumbers, updateStoredLicenseNumbers } from '../services/api.service';
import { getUsers } from '../services/manage-users.service';
import { take, debounce, get, uniqBy, keyBy, set } from 'lodash';
import styles from './styles';
import { Button } from '../button';
import { AddressForm } from './address-form';
import { noop } from 'lodash';
import { getConfig, getLicAddress, getRawLicense } from '../../actions/utils';

const ADD_NEW = 'ADD_NEW';

export default class LNSearch extends Component {
  state = {
    error: null,
    loading: true,
    licenseNumbersAll: [],
    licenseNumbers: [],
    allUsers: [],
    value: undefined,
    selectedOption: undefined,
    isNM: false,
    address: null,
    editAddress: {},
  };
  retrieve = async () => {
    try {
      const { accountsOnly } = this.props;
      const allLns = await getLicenseNumbers();

      const filterActive = get(this, 'props.filterActive', true);

      let licenseNumbersAll = []; 
      
      let allUsers = [];
      if (accountsOnly) {
        allUsers = await getUsers();
        const map = keyBy(allLns, 'License #');

        allUsers.map(u => {
          const company = get(u, 'data.company');
          if (company) {
            const primaryLn = get(company, 'License #');
            if (primaryLn && get(map, primaryLn)) {
              licenseNumbersAll.push(company);
            }
            const additionalLNs = get(company, 'licenseNumbers');
            if (additionalLNs && additionalLNs.map) {
              additionalLNs.map(ln => {
                const comp = get(map, ln);
                if (comp) { licenseNumbersAll.push(comp) }
              })
            }
          }
        })
        licenseNumbersAll = uniqBy(licenseNumbersAll, 'License #');
      } else {
        licenseNumbersAll = allLns;
      }
      licenseNumbersAll = licenseNumbersAll.filter(f => !!get(f, 'License #') && filterActive ? !!get(f, 'active', true) : true);
      const licenseNumbers = this.generateOptions(take(licenseNumbersAll, 100));
      this.setState({
        licenseNumbers,
        loading: false,
        allUsers,
        licenseNumbersAll: filterActive ? licenseNumbersAll.filter(f => !!get(f, 'active', true)) : licenseNumbersAll,
      }, this.props.onLoaded && this.props.onLoaded({ ...this.state, allLns }));
    } catch (err) {
      console.error(err);
    }
  }
  componentDidMount = () => {
    this.retrieve();
    // Seed the displayed value from a pre-populated form value (e.g. the
    // existing primary License # when editing an account) in case the value
    // is already present on first mount and componentDidUpdate never sees a
    // change. (card #221 R2 pre-population)
    if (this.props.value && this.state.value !== this.props.value) {
      this.setState({ value: this.props.value });
    }
  };
  componentDidUpdate = (prevProps, prevState) => {
    if(this.props.value !== prevProps.value && this.props.value !== this.state.value) {
      this.setState({
        value: this.props.value
      })
    }
    if (prevState.isNM !== this.state.isNM) {
      this.props.onNM && this.props.onNM(this.state.value);
    }
    if (this.props.accountsOnly !== prevProps.accountsOnly) {
      this.retrieve();
    }
  }
  generateOptions = (lns) => {
    const includeAddress = getConfig('orders.includeAddress', false);
    const includeDBA = getConfig('orders.includeDBA', false);

    const opts = lns.filter(l => !!get(l, 'License #')).map(l => {
      const addr = includeAddress ? getLicAddress(get(l, 'License #')) : null;
      const dba = includeDBA ? get(l, 'DBA') : null;
      const name = !!dba ? dba : get(l, 'Licensee');
      const value = `${get(l, 'License #')}`;
      const disabled = get(this, `props.disabledOpts`, []).includes(value);

      const content = (
        <span style={disabled ? { color: 'rgba(0,0,0,0.25)' } : {}}>
          <span>{`${name} - `}</span>
          <span css={css(`font-weight: bold;`)}>{`#${l['License #']}`}</span>
          {!!addr && <span>{` - ${addr}`}</span>}
        </span>
      );

      return (
        <Select.Option
          key={value}
          value={value}
          name={`${name} - #${l['License #']}${!!addr ? ` - ${addr}` : ''}`}
          disabled={disabled}
        >
          {disabled ? (
            <Tooltip title="Contact your admin to be added" placement="left">
              {content}
            </Tooltip>
          ) : content}
        </Select.Option>
      )
    });

    if (this.props.allowEdit) {
      opts.unshift((
        <Select.Option
          key={ADD_NEW}
          value={ADD_NEW}
        >
          <span>
            <span style={{ fontWeight: 'bold' }}>{`Add New License`}</span>
          </span>
        </Select.Option>
      ))
    }

    return opts;
  }
  getSelected = () => {
    const { licenseNumbersAll, selectedOption, value } = this.state;

    return licenseNumbersAll.find(l => get(l, 'License #') === (selectedOption || value)) || {};
  }
  onChange = (value) => {
    const isNM = (value || '').toLowerCase().indexOf('nm-') > -1; //not-medical
    this.setState({ value, isNM });
    if (isNM) {
      this.selectOption(value);
    }
  }
  onSearch = debounce((input) => {
    const { licenseNumbersAll } = this.state;

    this.setState({ loading: true });

    const val = input.toLowerCase();
    const includeAddress = getConfig('orders.includeAddress', false);
    const includeDBA = getConfig('orders.includeDBA', false);

    const licenseNumbers = licenseNumbersAll.filter(l => {
      const name = get(l, 'Licensee', '').toLowerCase();
      const ln = get(l, 'License #', '').toLowerCase();
      const addr = includeAddress ? getLicAddress(get(l, 'License #')).toLowerCase() : '';
      const dba = includeDBA ? `${get(l, 'DBA')}`.toLowerCase() : '';

      if (name.indexOf(val) > -1 || ln.indexOf(val) > -1 || addr.indexOf(val) > -1 || dba.indexOf(val) > -1) {
        return true;
      } else {
        return false;
      }
    })

    this.setState({
      licenseNumbers: take(this.generateOptions(licenseNumbers), 100),
      loading: false,
    });
  }, 800);
  findLic = (value) => {
    const lns = get(this.state, 'licenseNumbersAll', []);
    const lic = lns.find(l => get(l, 'License #') === value);

    const phone = '';// isEdit ? null : get(sState, 'user.company.phone', '');
    let text = ''

    if (lic) {
      const DBA = get(lic, 'DBA');
      const address = (() => {
        if (lic) {
          const street = get(lic, 'Street Address');
          const city = get(lic, 'City');
          const zip = get(lic, 'Zip');

          return [street, city, zip].filter(a => !!a).join(', ');
        }
      })(); 
      text = (address ? `Address: ${address}` : '') + (DBA ? `${address ? '- ' : ''}DBA: ${DBA}` : '') + (phone ? ` - Phone: ${phone}` : '');
    }

    return { lic, text }
  }
  selectOption = (val) => {
    const { onSelect } = this.props;
    
    if (!val) {
      return this.setState({
        value: undefined,
        selectedOption: undefined,
      }, () => onSelect && onSelect(val));
    }

    const { lic, text } = this.findLic(val);

    if (val == ADD_NEW) {
      return this.setState({ selectedOption: null, value: null, address: null, lic: null }, this.setEditAddress);
    }

    this.setState({ 
      selectedOption: val, 
      value: val,
      address: text,
      lic,
    }, () => onSelect && onSelect(val));
  }
  setEditAddress = () => {
    let form;
    const close = () => {
      this.setState({ editAddress: { visible: false } });
    }

    const onSave = () => {
      return new Promise((resolve, reject) => {
        form.validateFieldsAndScroll(async (err, vals) => {
          try {
            if (err) { return reject(err); }
            const stored = await getStoredLicenseNumbers();
            const selLicense = vals['License #'] ? vals : this.state.lic;
            const licNum = get(selLicense, 'License #');

            // Card #266 — without a license number there is nothing to key the
            // record on, and lodash `set` would happily write a literal
            // "undefined" entry into the shared tenant license store.
            if (!licNum) {
              return reject(new Error('A License Number is required.'));
            }

            // Card #266 — decide this BEFORE the store write, otherwise every
            // save looks like a pre-existing license. This modal doubles as
            // "Add New License" and "edit this license's address" (the pencil in
            // renderAddress); only the former is worth notifying admins about.
            // state.lic is null on the ADD_NEW path (selectOption) and populated
            // when editing, but a user can also type an already-known number
            // into the add form — hence the store/keymap checks too.
            const isNewLicense = !this.state.lic
              && !get(stored, licNum)
              && !getRawLicense(licNum);

            const newLic = {
              ...get(stored, licNum, {}),
              ...vals,
              // In edit mode the License # field is not rendered (address-form.js),
              // so `vals` omits it. Stamping it here keeps the record
              // self-describing for every downstream consumer.
              'License #': licNum,
            }

            set(stored, licNum, newLic)

            await updateStoredLicenseNumbers(stored);
            await window.LOADLICENSE();
            await this.retrieve();

            this.selectOption(licNum);

            close();

            if (isNewLicense) {
              this.props.onSaveNew && this.props.onSaveNew(newLic);
            }

            resolve(vals);
          } catch (err) {
            message.error(`Failed to update address: ${err.toString()}`);
            reject(err);
          }
        })
      })
    }

    this.setState({
      editAddress: {
        onClose: close,
        onCancel: close,
        onOk: onSave,
        onSubmit: onSave,
        visible: true,
        lic: this.state.lic,
        registerForm: f => form = f,
      }
    })
  }
  renderAddress = () => {
    const { address } = this.state;
    const { allowEdit } = this.props;

    return (
      <div css={css(styles.subText)}>
        {address}
        {allowEdit && <Button onClick={this.setEditAddress} css={css(styles.editButton)} size="small" icon="edit"></Button>}
      </div>
    )
  }
  render() {
    const { loading, licenseNumbers, value, address } = this.state;
    const { showAddress, allowEdit, onSaveNew, onNM, filterActive, canAdd: canAddProp, tooltip, accountsOnly, onLoaded, disabledOpts, _tooltip, ...autoCompleteProps } = this.props;

    const canAdd = get(this, 'props.canAdd', true);

    return (
      <div style={{ width: '100%', ...(this.props.style || {}) }}>
        <AutoComplete
          {...autoCompleteProps}
          style={{ width: '100%' }}
          placeholder={this.props.placeholder || 'Type to filter results'}
          onSearch={this.onSearch}
          onSelect={this.selectOption}
          dataSource={canAdd ? licenseNumbers : undefined}
          onChange={this.onChange}
          value={value}
          optionLabelProp={'name'}
        >
          <Input suffix={<Icon onClick={loading ? noop : () => { this.selectOption(null); this.onSearch('') }} type={loading ? "loading" : 'close'} />} />
        </AutoComplete>
        {showAddress && !!address && this.renderAddress()}
        <AddressForm {...this.state.editAddress} />
      </div>
    );
  }
};

LNSearch.defaultProps = {
  allowEdit: false,
  showAddress: false,
}